import { DataTypes, FormTypes, InputControllerTypes, InputDataSourceTypes } from "common/enums";
import { IDataStringExtraValue, IDefaultValue, IInputContentData, IRuleParemeters, IValidationInfos } from "./common.model";
import { Subject } from "rxjs";
import { InputContentDataType, extractValueWithExtraType } from "client/models";
import { isString } from "common/helper";

export interface IDetailInfo {
    evideInputContentGuid: string;
    description: string;
    title: string;
    sortOrder: number;
    isRequired: boolean;
    isMandatory: boolean;
    isHidden: boolean;
    hasDefaultValue: boolean,
    defaultValue: IDefaultValue,
    defaultValues: IDefaultValue[],
    isUnique: boolean;
    maxValue: string;
    minValue: string;
    regEx: string;
    detailVisible: boolean;
    dataType: DataTypes;
    dataSourceType: InputDataSourceTypes;
    formType: FormTypes;
    gridEdit: boolean;
    gridHeaderEdit: boolean;
    controllerType: InputControllerTypes;
    inputContentDatas: IInputContentData;
    // Option keys (dataKey) in display order. The backend sends this because inputContentDatas is a
    // JSON object and object key order is not reliable for numeric-string keys.
    inputContentDataOrder?: string[];
    // dataKey -> group heading for <optgroup> rendering. Only headed options appear here.
    inputContentDataHeadings?: { [dataKey: string]: string };
    validationInfos: IValidationInfos[];
    variantTagGuids?: string[];
    isReferenceField?: boolean;
}

export class DetailInfo {

    public $valueChange = new Subject<boolean>();
    private $value: string | Array<string> | IDataStringExtraValue | IDataStringExtraValue[] = '';
    private $key: string | Array<string> | IDataStringExtraValue | IDataStringExtraValue[] = '';
    public isFieldLoading = false;

    public isHiddenByCurrentVariant = false;

    // True for the contact reference field — the form pre-fills it from the non-rule generator on open.
    public isReferenceField = false;

    // An optional line shown under the field's title in a quieter, italic style — context the host
    // screen knows and the form does not (the Support Assessment review: "Previous assessment: 3").
    // Set by the host after the fields load; never part of the form definition.
    public hint?: string;

    constructor(
        public readonly evideInputContentGuid: string,
        public readonly title: string,
        public readonly description: string,
        public readonly sortOrder: number,
        public readonly isRequired: boolean,
        public readonly isMandatory: boolean,
        public readonly isHidden: boolean,
        public readonly hasDefaultValue: boolean,
        public defaultValue: IDefaultValue,
        public defaultValues: IDefaultValue[],
        public readonly isUnique: boolean,
        public readonly maxValue: string,
        public readonly minValue: string,
        public readonly regEx: string,
        public readonly detailVisible: boolean,
        public readonly dataType: DataTypes,
        public readonly dataSourceType: InputDataSourceTypes,
        public readonly formType: FormTypes,
        public readonly controllerType: InputControllerTypes,
        public readonly gridEdit: boolean,
        public readonly gridHeaderEdit: boolean,
        public readonly inputContentDatas: ReadonlyArray<InputContentDataType>,
        public ruleParemeters: IRuleParemeters,
        public readonly validationInfos: IValidationInfos[],
        public readonly variantTagGuids: string[] = []
    ) { }

    public static fromJson(data: IDetailInfo, ruleParemeters: IRuleParemeters = {}) {
        const dict = data.inputContentDatas || {};
        // Render options in the order the backend specifies (inputContentDataOrder), not Object.keys
        // order — a JS object reorders numeric-string keys, which would break a configured option order.
        // Keep only keys still present in the dictionary, then append any dictionary keys missing from
        // the order list (legacy data / responses without the order array).
        const order = (data.inputContentDataOrder ?? []).filter((k) => Object.prototype.hasOwnProperty.call(dict, k));
        const orderedKeys = order.concat(Object.keys(dict).filter((k) => !order.includes(k)));
        const headings = data.inputContentDataHeadings || {};
        const inputContentDatas: ReadonlyArray<InputContentDataType> = orderedKeys.map(
            (key) => {
                const [value, extraValueType] = extractValueWithExtraType(dict[key]);
                return { key, value: value, extraValueType, heading: headings[key] || undefined };
            }
        );
        const obj = new this(
            data.evideInputContentGuid, data.title, data.description,
            data.sortOrder, data.isRequired, data.isMandatory, data.isHidden,
            data.hasDefaultValue, data.defaultValue !== null ? data.defaultValue : '', data.defaultValues, data.isUnique,
            data.maxValue, data.minValue, data.regEx, data.detailVisible,
            data.dataType, data.dataSourceType, data.formType,
            data.controllerType, data.gridEdit, data.gridHeaderEdit,
            inputContentDatas, ruleParemeters, data.validationInfos,
            data.variantTagGuids ?? []
        );
        obj.isReferenceField = data.isReferenceField ?? false;
        return obj;
    }

    
    public computeHiddenByVariant(selectedVariantGuid: string | null | undefined): boolean {
        const tagGuids = this.variantTagGuids;
        if (!tagGuids || tagGuids.length === 0) return false;
        if (!selectedVariantGuid) return true;
        return !tagGuids.includes(selectedVariantGuid);
    }

    public set value(value: string | Array<string> | IDataStringExtraValue | IDataStringExtraValue[]) {
        this.$value = value;
        this.$valueChange.next(true);
    }
    public get value(): string | Array<string> | IDataStringExtraValue | IDataStringExtraValue[] {
        return this.$value;
    }

    public set key(key: string | Array<string> | IDataStringExtraValue | IDataStringExtraValue[]) {
        this.$key = key;
        this.$valueChange.next(true);
    }

    public get key(): string | Array<string> | IDataStringExtraValue | IDataStringExtraValue[] {
        return this.$key;
    }

    public get displayValue(): string {
        if (isString(this.$value)) {
            return this.$value as string;
        }
        if (Array.isArray(this.$value)) {
            const values = this.$value;
            let str = '';
            values.forEach((val, i) => {
                if (isString(val)) {
                    str += ` ${val}`;
                } else if (val.dataStringValue) {
                    str += ` ${val.dataStringValue}${val.dataStringExtraValue ? `(${val.dataStringExtraValue})` : ''}`;
                }
                if (i < values.length - 1) {
                    str += ',';
                }
            });
            return str;
        }
        return this.$value.dataStringValue + `${this.$value.dataStringExtraValue ? `(${this.$value.dataStringExtraValue})` : ''}`;
    }

    public get displayStringValue(): string {
        return this.displayValue as string;
    }

    public clone() {
        const obj = new DetailInfo(
            this.evideInputContentGuid,
            this.title,
            this.description,
            this.sortOrder,
            this.isRequired,
            this.isMandatory,
            this.isHidden,
            this.hasDefaultValue,
            this.defaultValue,
            this.defaultValues,
            this.isUnique,
            this.maxValue,
            this.minValue,
            this.regEx,
            this.detailVisible,
            this.dataType,
            this.dataSourceType,
            this.formType,
            this.controllerType,
            this.gridEdit,
            this.gridHeaderEdit,
            // Deep-copy the option list so clones own independent option objects. Some leaf controls
            // (e.g. checkbox) store selection state (checked / extraValue) ON these option objects, so
            // a shared reference would leak one clone's selection into every other clone — visible as
            // repeatable-grid rows sharing checkbox/extra state across rows.
            this.inputContentDatas.map((o) => ({ ...o })),
            this.ruleParemeters,
            this.validationInfos,
            this.variantTagGuids
        );
        obj.isReferenceField = this.isReferenceField;
        obj.hint = this.hint;

        return obj;
    }
}